import { Link } from 'react-router-dom';
import { useState } from 'react';
import PageSEO from '@/components/feature/PageSEO';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' || (value as string).trim() === '') {
          urlEncoded.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9n4klgbdkpk99j7ggcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = (parsed && (parsed.meta as Record<string, unknown>)?.message as string)
          || (parsed?.message as string)
          || (parsed && (parsed.meta as Record<string, unknown>)?.detail as string)
          || responseText
          || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '关于我们 - 天天影院',
    url: 'https://tiantiancinema.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '天天影院',
      url: 'https://tiantiancinema.com',
    },
    about: {
      '@type': 'Organization',
      name: '天天影院',
      url: 'https://tiantiancinema.com',
      description: '天天影院拥有最新电影大全、动作片在线、喜剧片免费看、科幻片高清播放及经典电影全集，极速播放不卡顿，海量片库免费高清在线看。',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://tiantiancinema.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://tiantiancinema.com/about' },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '天天影院需要付费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '天天影院上的所有电影资源均为免费在线观看，无需任何付费或会员注册。我们致力于为广大影迷提供最新电影大全、动作片在线、喜剧片免费看、科幻片高清播放以及经典电影全集，全部免费高清在线看。',
        },
      },
      {
        '@type': 'Question',
        name: '天天影院支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '天天影院支持电脑、平板、手机等所有主流设备在线观看。只要您的设备支持浏览器，就可以随时随地通过天天影院免费在线观看最新电影大全，畅享极速播放体验。',
        },
      },
      {
        '@type': 'Question',
        name: '天天影院的电影画质如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '天天影院采用先进的视频压缩和传输技术，提供高清甚至超高清画质的电影在线观看体验。所有动作片、喜剧片、科幻片和经典电影均支持高清播放，画质清晰流畅。',
        },
      },
      {
        '@type': 'Question',
        name: '天天影院更新频率是多少？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '天天影院每天更新最新电影资源，确保用户能够第一时间观看到最新的动作片、喜剧片、科幻片等新上映电影。我们的片库也在持续扩充中，为用户提供更丰富的观影选择。',
        },
      },
    ],
  };

  return (
    <>
      <PageSEO
        title="关于我们 - 天天影院 | 品牌介绍 口碑评价 联系方式"
        description="天天影院品牌介绍：了解天天影院的品牌故事、用户口碑评价和联系方式。天天影院拥有最新电影大全、动作片在线、喜剧片免费看、科幻片高清播放及经典电影全集。"
        keywords="天天影院,关于我们,品牌介绍,联系方式,电影平台"
        canonicalUrl="https://tiantiancinema.com/about"
        ogTitle="关于我们 - 天天影院"
        ogDescription="了解天天影院的品牌故事、用户口碑评价和联系方式。天天影院拥有最新电影大全，极速播放不卡顿。"
        ogUrl="https://tiantiancinema.com/about"
        structuredData={[breadcrumbSchema, websiteSchema, faqSchema]}
      />

      <Breadcrumb />

      <main className="w-full max-w-[1200px] mx-auto px-4 md:px-6 pb-16">
        <article>
          <header className="mb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-3">
              关于天天影院
            </h1>
            <p className="text-sm text-foreground-500 max-w-2xl">
              天天影院致力于为广大影迷提供最新电影大全，覆盖动作片、喜剧片、科幻片、经典电影等丰富类型，极速高清播放，免费在线观看。
            </p>
          </header>

          <section className="mb-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-xl font-heading font-bold text-foreground-50 mb-4">
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-400 leading-relaxed">
                  <p>
                    天天影院创立于2020年，由一群热爱电影的年轻人共同打造。我们深知在繁忙的生活中，能够随时随地欣赏一部好电影是多么珍贵的体验。因此，我们倾力打造了天天影院这个平台，让每一位用户都能在这里找到自己喜欢的电影。
                  </p>
                  <p>
                    从最初的几十部电影资源开始，天天影院逐步发展壮大。今天，我们已经拥有了涵盖<strong>动作片、喜剧片、科幻片、经典电影、爱情片、恐怖片、动画片、纪录片</strong>等八大类型的海量片库。无论是想看最新上映的<strong>动作片在线</strong>观看，还是想回味<strong>经典电影全集</strong>，天天影院都能满足您的需求。
                  </p>
                  <p>
                    我们的技术团队不断优化播放体验，采用先进的视频编码和CDN加速技术，确保<strong>极速播放</strong>不卡顿。无论您使用电脑、平板还是手机，都能享受流畅的<strong>高清播放</strong>体验。最重要的是，天天影院始终坚持<strong>免费电影</strong>的理念——所有资源完全免费，无需注册、无需付费。
                  </p>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20home%20cinema%20room%20with%20large%20screen%20comfortable%20seating%20warm%20ambient%20lighting%20dark%20walls%20cozy%20atmosphere%20professional%20interior%20photography&width=800&height=500&seq=about-cinema-01&orientation=landscape"
                  alt="天天影院的观影体验"
                  title="天天影院 - 极致观影体验"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-xl font-heading font-bold text-foreground-50 mb-6">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: '张先生',
                  role: '资深影迷',
                  avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Chinese%20man%20in%20his%2030s%20friendly%20smile%20warm%20lighting%20clean%20background%20headshot&width=100&height=100&seq=avatar-01&orientation=squarish',
                  text: '天天影院真的是我每天必上的网站！最新电影大全更新速度很快，动作片在线观看画质超清，科幻片高清播放的效果比某些付费平台还好。最重要的是完全免费，强烈推荐！',
                  rating: 5,
                },
                {
                  name: '李女士',
                  role: '电影爱好者',
                  avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Chinese%20woman%20in%20her%2020s%20warm%20smile%20natural%20lighting%20clean%20background%20headshot&width=100&height=100&seq=avatar-02&orientation=squarish',
                  text: '自从发现了天天影院，周末的娱乐活动就变成了在家看电影。喜剧片免费看资源丰富，经典电影全集更是让我重温了很多老片。极速播放不卡顿，体验非常好！',
                  rating: 5,
                },
                {
                  name: '王同学',
                  role: '大学生',
                  avatar: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20young%20Chinese%20man%20student%20friendly%20smile%20warm%20lighting%20clean%20background%20headshot&width=100&height=100&seq=avatar-03&orientation=squarish',
                  text: '作为一个学生党，能免费在线看高清电影简直太棒了！天天影院的电影大全分类很清晰，找片非常方便。动作片在线、科幻片高清播放，每次都能找到想看的。',
                  rating: 5,
                },
              ].map((review, index) => (
                <div
                  key={index}
                  className="p-6 rounded-xl bg-background-100 border border-background-300/30"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden shrink-0">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground-100">{review.name}</h3>
                      <p className="text-xs text-foreground-500">{review.role}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-primary-500 text-sm"></i>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-400 leading-relaxed">
                    {review.text}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-xl font-heading font-bold text-foreground-50 mb-6">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-4 p-4 rounded-xl bg-background-100 border border-background-300/30">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/15 shrink-0">
                    <i className="ri-mail-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-100 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-400">contact@tiantiancinema.com</p>
                    <p className="text-xs text-foreground-600 mt-0.5">我们会在24小时内回复您的邮件</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-background-100 border border-background-300/30">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/15 shrink-0">
                    <i className="ri-phone-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-100 mb-1">客服电话</h3>
                    <p className="text-sm text-foreground-400">400-888-6688</p>
                    <p className="text-xs text-foreground-600 mt-0.5">服务时间：每天 9:00 - 22:00</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-background-100 border border-background-300/30">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/15 shrink-0">
                    <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-100 mb-1">公司地址</h3>
                    <p className="text-sm text-foreground-400">北京市朝阳区建国路88号SOHO现代城A座15层</p>
                    <p className="text-xs text-foreground-600 mt-0.5">邮编：100022</p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-background-100 border border-background-300/30 p-6">
                <h3 className="text-sm font-heading font-semibold text-foreground-100 mb-4">
                  给我们留言
                </h3>
                <form className="space-y-4" data-readdy-form="true" onSubmit={handleSubmit}>
                  <input
                    type="text"
                    name="name"
                    placeholder="您的姓名"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-300/50 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="您的邮箱"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-300/50 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                  />
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="请输入您的留言（500字以内）"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-300/50 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                  ></textarea>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden={true}
                    readOnly
                    className="form-honeypot"
                  />
                  {formStatus === 'success' && (
                    <div className="p-3 rounded-lg bg-primary-500/15 border border-primary-500/30 text-sm text-primary-400 text-center">
                      留言发送成功！我们会尽快回复您。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-3 rounded-lg bg-accent-500/15 border border-accent-500/30 text-sm text-accent-400 text-center">
                      {formError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 whitespace-nowrap rounded-full bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送留言'}
                  </button>
                </form>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-foreground-50 mb-6">
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                {
                  q: '天天影院需要付费吗？',
                  a: '天天影院上的所有电影资源均为免费在线观看，无需任何付费或会员注册。我们致力于为广大影迷提供最新电影大全、动作片在线、喜剧片免费看、科幻片高清播放以及经典电影全集，全部免费高清在线看。',
                },
                {
                  q: '天天影院支持哪些设备观看？',
                  a: '天天影院支持电脑、平板、手机等所有主流设备在线观看。只要您的设备支持浏览器，就可以随时随地通过天天影院免费在线观看最新电影大全，畅享极速播放体验。',
                },
                {
                  q: '天天影院的电影画质如何？',
                  a: '天天影院采用先进的视频压缩和传输技术，提供高清甚至超高清画质的电影在线观看体验。所有动作片、喜剧片、科幻片和经典电影均支持高清播放，画质清晰流畅。',
                },
                {
                  q: '天天影院更新频率是多少？',
                  a: '天天影院每天更新最新电影资源，确保用户能够第一时间观看到最新的动作片、喜剧片、科幻片等新上映电影。我们的片库也在持续扩充中，为用户提供更丰富的观影选择。',
                },
              ].map((faq, index) => (
                <details
                  key={index}
                  className="group rounded-xl bg-background-100 border border-background-300/30 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <h4 className="text-sm font-medium text-foreground-100 pr-4">{faq.q}</h4>
                    <div className="w-6 h-6 flex items-center justify-center shrink-0">
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-400 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </article>
      </main>
    </>
  );
}